import type { PersonaId } from "@petci/domain";
import { p1Marsha } from "@petci/fixtures";
import { formatDate, formatDurationLong } from "./format";

// P1's spoilage insight derives BOTH its date and its duration from the
// persona's own outage log so the insight, the outage history, the canned AI
// answer, and the live AI all tell one story (F-B, H0.6). The Bight Park event
// is a genuine past record.
const p1Outage = p1Marsha.outages[0];
const P1_BIGHT_PARK_OUTAGE_DATE = formatDate(p1Outage?.startedAt);
const P1_BIGHT_PARK_OUTAGE_DURATION = formatDurationLong(p1Outage?.durationMinutes ?? 0);

/**
 * R-D: key + params for the outage-duration leadFigure. `formatDurationLong`
 * emits English unit words ("2 hr 40 min"), so the web card resolves this key
 * instead; mobile keeps the English field byte-identical (Phase 3 does the
 * general cross-surface pass). Mirrors formatDurationLong's three branches so
 * the two can never disagree about which shape a duration takes.
 */
function durationLeadFigure(totalMinutes: number): {
  key: string;
  params: Record<string, string>;
} {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) {
    return { key: "insights.duration.minutesOnly", params: { minutes: String(minutes) } };
  }
  if (minutes === 0) {
    return { key: "insights.duration.hoursOnly", params: { hours: String(hours) } };
  }
  return {
    key: "insights.duration.hoursMinutes",
    params: { hours: String(hours), minutes: String(minutes) },
  };
}

const P1_OUTAGE_DURATION_LEAD = durationLeadFigure(p1Outage?.durationMinutes ?? 0);

/**
 * P3 kitchen-anomaly recency — single source so the insight body and the home
 * Recent Activity feed cannot drift (H0.4). Relative window, no date literal.
 *
 * `_KEY` is the i18n key for the SAME phrase in mid-sentence (lowercase) form —
 * the web insight body resolves it as the `{recency}` ICU param (W6). Its en-TC
 * value MUST equal `P3_KITCHEN_ANOMALY_RECENCY` verbatim; the byte-identical
 * snapshot pin (insights.i18n.test.ts) enforces it. Kept here, beside the
 * English const, so the two can only change together. (Home's own recency label
 * uses the Titlecase `home.time.*` keys — a different presentation of the same
 * const, not a byte-identical mid-sentence form.)
 */
export const P3_KITCHEN_ANOMALY_RECENCY = "last week";
export const P3_KITCHEN_ANOMALY_RECENCY_KEY = "insights.recencyLastWeek";

/**
 * Raw, locale-neutral inputs for the two insight bodies that interpolate dynamic
 * values. The WEB card formats them locale-correctly (D12) — shared-ui holds no
 * active locale, so it cannot pre-format. Absent on static bodies.
 */
export interface InsightBodyParams {
  /**
   * ISO date for the `{date}` param; web formats via `formatDate(iso, tag)` (D12).
   * `| undefined` is explicit so the source (`outages[0]?.startedAt`) assigns
   * under `exactOptionalPropertyTypes` — `formatDate` maps undefined → "—".
   */
  date?: string | undefined;
  /** i18n key for the `{recency}` param; web resolves it via `t()`. */
  recencyKey?: string | undefined;
}

export interface Insight {
  id: string;
  /**
   * English SSoT for the lead figure — consumed verbatim by MOBILE. Most values
   * are bare numerals ("$87", "+8.7%") and so are locale-neutral here; the two
   * that carry UNIT VOCABULARY ("2 hr 40 min", "32 kWh/day") also set
   * `leadFigureKey`, which web prefers (R-D). The general cross-surface unit
   * pass ships in Phase 3 with mobile.
   */
  leadFigure: string;
  /** R-D i18n key for `leadFigure`; en-TC resolves byte-identical. */
  leadFigureKey?: string;
  /** Params for `leadFigureKey`. Strings, so ICU never number-groups them. */
  leadFigureParams?: Record<string, string>;
  /**
   * English SSoT for the insight body — still consumed verbatim by MOBILE
   * (Phase 3) and the byte-identical en-TC fallback. Web prefers `bodyKey`.
   */
  body: string;
  /** W6 i18n key for `body`; en-TC resolves byte-identical (snapshot pin). */
  bodyKey?: string;
  /** Raw params for `bodyKey` when the body interpolates (date / recency). */
  bodyParams?: InsightBodyParams;
  /** English SSoT for the CTA (mobile / fallback). Web prefers `ctaKey`. */
  cta: string | null;
  /** W6 i18n key for `cta`; en-TC resolves byte-identical (snapshot pin). */
  ctaKey?: string;
  action:
    // `intentId` (D4) names the canned intent the assistant resolves on the
    // fallback path — resolution is by id, never by prompt-string matching, so
    // rewording `prompt` (live-AI/display text) can never re-break the insight
    // → answer flow (the finding #2 defect). Every id must resolve via
    // `INTENTS_BY_ID` — enforced by insights.intent-wiring.test.ts.
    | { kind: "assistant"; prompt: string; intentId: string }
    | { kind: "route"; webPath: string; mobilePath: string };
}

export const INSIGHTS_PER_PERSONA: Record<PersonaId, Insight[]> = {
  P1: [
    {
      id: "p1-solar-april",
      leadFigure: "$87",
      body: "in solar export credit this cycle — your best on record (38% of usage offset).",
      bodyKey: "insights.p1SolarApril.body",
      cta: "Tell me more",
      ctaKey: "insights.cta.tellMeMore",
      action: {
        kind: "assistant",
        prompt: "How much did my solar save me this cycle, and how does that compare to last year?",
        intentId: "solar-value",
      },
    },
    {
      id: "p1-outage-spoilage",
      leadFigure: P1_BIGHT_PARK_OUTAGE_DURATION,
      leadFigureKey: P1_OUTAGE_DURATION_LEAD.key,
      leadFigureParams: P1_OUTAGE_DURATION_LEAD.params,
      body: `Bight Park feeder outage on ${P1_BIGHT_PARK_OUTAGE_DATE} — you may qualify for spoilage credit up to $25.`,
      bodyKey: "insights.p1OutageSpoilage.body",
      // `{date}` is the raw ISO; the web card localizes it via formatDate(iso, tag)
      // (D12), so en-TC stays byte-identical (tag omitted) and es/ht render dates
      // locale-correct. Same source the English body derives P1_BIGHT_PARK_OUTAGE_DATE from.
      bodyParams: { date: p1Outage?.startedAt },
      cta: "Claim credit",
      ctaKey: "insights.cta.claimCredit",
      action: {
        kind: "route",
        webPath: "/services/damage-claims",
        mobilePath: "/(app)/services/damage-claims",
      },
    },
    {
      id: "p1-usage-trend",
      leadFigure: "+8.7%",
      body: "usage this cycle vs last — mostly weekend AC overnight (12% above weekday average).",
      bodyKey: "insights.p1UsageTrend.body",
      cta: "Tell me more",
      ctaKey: "insights.cta.tellMeMore",
      action: {
        kind: "assistant",
        prompt: "Why is my usage higher this cycle than last? Break it down by day-of-week and time-of-day.",
        intentId: "p1-usage-breakdown",
      },
    },
  ],
  P2: [
    {
      id: "p2-dive-day-cost",
      leadFigure: "$22",
      body: "average electricity cost per dive day this cycle — air compressor cycle is your biggest line.",
      bodyKey: "insights.p2DiveDayCost.body",
      cta: "Tell me more",
      ctaKey: "insights.cta.tellMeMore",
      action: {
        kind: "assistant",
        prompt: "What's my electricity cost per dive day this month, and how could I reduce it?",
        intentId: "p2-dive-day-cost",
      },
    },
    {
      id: "p2-weekly-pattern",
      leadFigure: "+15%",
      body: "Tue/Wed usage bump every week — likely tank-fill compressor runs after weekend dives.",
      bodyKey: "insights.p2WeeklyPattern.body",
      cta: "Tell me more",
      ctaKey: "insights.cta.tellMeMore",
      action: {
        kind: "assistant",
        prompt: "Why does my usage spike on Tuesdays and Wednesdays each week?",
        intentId: "p2-weekly-pattern",
      },
    },
    {
      id: "p2-grand-turk-fuel",
      leadFigure: "0.1538",
      body: "Grand Turk fuel factor this month — slightly below Provo's 0.1564 due to fuel-shipment timing.",
      bodyKey: "insights.p2GrandTurkFuel.body",
      cta: "Tell me more",
      ctaKey: "insights.cta.tellMeMore",
      action: {
        kind: "assistant",
        prompt: "Why is the fuel factor for Grand Turk different from Providenciales?",
        intentId: "p2-grand-turk-fuel",
      },
    },
  ],
  P3: [
    {
      id: "p3-kitchen-anomaly",
      leadFigure: "+18%",
      body: `Kitchen & F&B meter Tue–Thu ${P3_KITCHEN_ANOMALY_RECENCY} — matches the brunch event on your calendar.`,
      bodyKey: "insights.p3KitchenAnomaly.body",
      // `{recency}` resolves to the SAME phrase P3_KITCHEN_ANOMALY_RECENCY holds,
      // via its co-located `_KEY` (en-TC byte-identical, es/ht localized).
      bodyParams: { recencyKey: P3_KITCHEN_ANOMALY_RECENCY_KEY },
      cta: "View detail",
      ctaKey: "insights.cta.viewDetail",
      action: {
        kind: "route",
        webPath: "/hotel/portfolio",
        mobilePath: "/(app)/desktop-only/hotel-portfolio",
      },
    },
    {
      id: "p3-demand-near-miss",
      leadFigure: "412 / 450 kW",
      body: "Peak demand last Wednesday — deferring laundry to 2–6 AM could prevent the next threshold breach.",
      bodyKey: "insights.p3DemandNearMiss.body",
      cta: "Tell me more",
      ctaKey: "insights.cta.tellMeMore",
      action: {
        kind: "assistant",
        prompt: "How close am I to my 450 kW peak demand threshold, and what's the impact of crossing it?",
        intentId: "demand-peak-alert",
      },
    },
    {
      id: "p3-esg-offset",
      // Corrected to the fixture (H0): hotelKpi.pctRenewable = 8 vs benchmark 12
      // (BELOW the peer average) — the prior "14.2% … 12% better than average"
      // contradicted the KPI. Honest framing plus the board-approved +200 kW
      // upside (solar.phase2Plan).
      leadFigure: "8%",
      body: "of this cycle's usage offset by your 80 kW CORE solar — the TCI luxury benchmark is 12%, and the board-approved +200 kW closes the gap.",
      bodyKey: "insights.p3EsgOffset.body",
      cta: "Tell me more",
      ctaKey: "insights.cta.tellMeMore",
      action: {
        kind: "assistant",
        prompt: "How does my solar offset percentage compare to other TCI hotels? Cite the benchmark.",
        intentId: "p3-solar-offset",
      },
    },
  ],
  P4: [
    {
      id: "p4-villa-anomaly",
      leadFigure: "32 kWh/day",
      leadFigureKey: "insights.leadFigure.kwhPerDay",
      leadFigureParams: { value: "32" },
      // Hypothesis aligned to the investigation card + anomalyBadge (H0.5):
      // "water heater? (baseline 0.8 kWh)" — one hypothesis across both surfaces.
      body: "Villa Sea Breeze during 2-week vacancy — possible always-on equipment, water heater suspected.",
      bodyKey: "insights.p4VillaAnomaly.body",
      cta: "Schedule technician",
      ctaKey: "insights.cta.scheduleTechnician",
      action: {
        kind: "route",
        webPath: "/pm/anomaly/30556",
        mobilePath: "/(app)/pm/30556",
      },
    },
    {
      id: "p4-portfolio-trend",
      // Corrected to the ledger (H0): current 5,568 kWh vs prior 5,580 kWh is
      // essentially flat (−0.2%), not "+4.1%". Cross-references the Sea Breeze
      // anomaly so the aggregate-trend and single-property-anomaly stories agree.
      leadFigure: "-0.2%",
      body: "portfolio usage this cycle vs last (5,568 vs 5,580 kWh) — seasonal-steady across 4 properties; the Sea Breeze anomaly is the one exception.",
      bodyKey: "insights.p4PortfolioTrend.body",
      cta: "Tell me more",
      ctaKey: "insights.cta.tellMeMore",
      action: {
        kind: "assistant",
        prompt: "How is my portfolio usage trending this cycle? Break it down by property.",
        intentId: "p4-portfolio-trend",
      },
    },
    {
      id: "p4-sandy-cove-savings",
      // Renamed from "Conch Bay" (not in the managed roster) to Villa Sandy Cove
      // (account 30558) — H0.3. The $66 is framed as a saving vs the pre-audit
      // run-rate (no fabricated metered prior).
      leadFigure: "-$66",
      body: "Villa Sandy Cove run-rate vs the pre-audit baseline — a tenant-turnover meter audit caught a 24/7 well pump.",
      bodyKey: "insights.p4SandyCoveSavings.body",
      cta: "Tell me more",
      ctaKey: "insights.cta.tellMeMore",
      action: {
        kind: "assistant",
        prompt: "What changed at Villa Sandy Cove to lower its bill after the tenant-turnover audit?",
        intentId: "p4-property-savings",
      },
    },
  ],
  P5: [],
};
